import {ConversationStageType} from '@frontapp/insights/conversation_stages';
import React, {ReactNode} from 'react';

import {Localizer} from '../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../libs/shared/core/src/helpers/internationalization/message';
import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {makeEnumTypeGuard} from '../../../libs/shared/core/src/models/api/types/enumTypes';
import {UserTheme} from '../../../libs/shared/ui/src/theme/themeTypes';
import {
  BasicActivityConversationStageUpdateModel,
  BasicActivityModel,
} from '../../../src/models/api/activityModel';

interface ConversationStageUpdateColor {
  (theme: UserTheme): {
    backgroundColor: string;
    color: string;
    borderColor: string;
    hoverColor: string;
  };
}

type ConversationStageUpdateTooltipContent = (localize: Localizer) => string;

export const isConversationStageType = makeEnumTypeGuard(ConversationStageType);

export function isBasicActivityConversationStageUpdateModel(
  activity: BasicActivityModel,
): activity is BasicActivityConversationStageUpdateModel {
  return 'conversationStageType' in activity && isConversationStageType(activity.conversationStageType);
}

/** Activity labels for each conversation stage type. */
export const activityConversationStageUpdateLabels: Record<ConversationStageType, ReactNode> = {
  [ConversationStageType.RESOLVED]: (
    <Trans id="activity-conversation-stage-update-label-resolved">resolved</Trans>
  ),
  [ConversationStageType.UNRESOLVED]: (
    <Trans id="activity-conversation-stage-update-label-unresolved">unresolved</Trans>
  ),
  [ConversationStageType.IGNORED]: (
    <Trans id="activity-conversation-stage-update-label-ignored">no action needed</Trans>
  ),
};

/** Activity colors palette for each conversation stage type. */
export const activityConversationStageUpdateColors: Record<
  ConversationStageType,
  ConversationStageUpdateColor
> = {
  [ConversationStageType.RESOLVED]: (theme) => ({
    backgroundColor: theme.palette.green.shade20,
    color: theme.palette.green.shade50,
    borderColor: theme.palette.green.shade50,
    hoverColor: theme.palette.green.shade30,
  }),
  [ConversationStageType.UNRESOLVED]: (theme) => ({
    backgroundColor: theme.palette.blue.shade20,
    color: theme.palette.blue.shade50,
    borderColor: theme.palette.blue.shade40,
    hoverColor: theme.palette.blue.shade30,
  }),
  [ConversationStageType.IGNORED]: (theme) => ({
    backgroundColor: theme.greys.shade20,
    color: theme.greys.shade70,
    borderColor: theme.greys.shade70,
    hoverColor: theme.greys.shade40,
  }),
};

/** Activity tooltip content for each conversation stage type. */
export const activityConversationStageUpdateTooltipContent: Record<
  ConversationStageType,
  ConversationStageUpdateTooltipContent
> = {
  [ConversationStageType.RESOLVED]: (localize) =>
    localize(
      defineMessage({
        id: 'activity-stage-update-resolved-tooltip-message',
        message: 'is a conversation stage indicating that the work on a conversation is done.',
      }),
    ),

  [ConversationStageType.UNRESOLVED]: (localize) =>
    localize(
      defineMessage({
        id: 'activity-stage-update-unresolved-tooltip-message',
        message: "is a conversation stage indicating that there's still work to do on the conversation.",
      }),
    ),
  [ConversationStageType.IGNORED]: (localize) =>
    localize(
      defineMessage({
        id: 'activity-stage-update-ignored-tooltip-message',
        message: "is a conversation stage indicating that this conversation doesn't require teammate work.",
      }),
    ),
};
